import { ItemWithId } from "types/ItemWithId";

/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

type ItemIdSelector<T> = (item: T) => string | undefined;

function defaultCollectionSelector<T extends ItemWithId>(item: T): string {
  return item.id;
}

type Updates<T> = Partial<T> | ((prev: Readonly<T>) => Partial<T>);

export function updateIfExists<T>(
  collection: Record<string, T>,
  targetId: string,
  update: Updates<T>
): Record<string, T>;

export function updateIfExists<T>(
  collection: T[],
  targetId: string,
  updates: Updates<T>,
  idSelector: ItemIdSelector<T>
): T[];

export function updateIfExists<T extends ItemWithId>(
  collection: T[],
  targetId: string,
  updates: Updates<T>
): T[];

export function updateIfExists<T>(
  collection: T[] | Record<string, T>,
  targetId: string,
  updates: Updates<T>,
  idSelector?: ItemIdSelector<T>
): T[] | Record<string, T> {
  const updateFunction: (prev: Readonly<T>) => Partial<T> =
    updates instanceof Function ? updates : (): Partial<T> => updates;

  if (collection instanceof Array) {
    const defaultedIdSelector = idSelector ?? defaultCollectionSelector;
    return collection.map((item) => {
      if (defaultedIdSelector(item as any) !== targetId) {
        return item;
      }
      return {
        ...item,
        ...updateFunction(item),
      };
    });
  } else {
    if (!collection[targetId]) {
      return collection;
    }

    return {
      ...collection,
      [targetId]: {
        ...collection[targetId],
        ...updateFunction(collection[targetId]),
      },
    };
  }
}
